import { useAppForm } from '@new/common/hooks/form';
import { formOptions } from '@tanstack/react-form';
import { Link, useNavigate } from '@tanstack/react-router';
import { FC, FormEvent } from 'react';
import z from 'zod';
import { useLoginMutation, httpClient, teachurKeys } from '@teachur/http-client';
import { useQueryClient } from '@tanstack/react-query';
import { checkboxValidation, emailValidation } from '../../schemas/validations';
import { getErrorMessage } from '@new/lib';
import { toast } from 'sonner';

export const loginSchema = z.object({
  email: emailValidation,
  password: z.string().min(1, 'Password is required'),
  remember: checkboxValidation,
});

export const loginFormOpts = formOptions({
  defaultValues: {
    email: '',
    password: '',
    remember: false,
  },
});

export const LoginForm: FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { mutateAsync: login } = useLoginMutation({
    onSuccess: (data) => {
      localStorage.setItem('accessToken', data.accessToken);
      httpClient.setAuthToken(data.accessToken);

      if (data.accessToken) {
        queryClient.invalidateQueries({ queryKey: teachurKeys.users.currentUser() });
        navigate({ to: '/' });
      }
    },
    onError: (error: unknown) => {
      const message = getErrorMessage(error, 'Invalid email or password');
      toast.error(message);
    },
  });
  const form = useAppForm({
    ...loginFormOpts,
    validators: {
      onChange: ({ value }) => {
        const parsed = loginSchema.safeParse(value);

        if (parsed.success) {
          return { fields: {} };
        }

        const errors: Record<string, string> = {};

        parsed.error.issues.forEach((issue) => {
          const field = issue.path[0];
          if (typeof field === 'string') {
            errors[field] = issue.message;
          }
        });

        return { fields: errors };
      },

      onSubmit: ({ value }) => {
        const parsed = loginSchema.safeParse(value);
        if (!parsed.success) {
          const errors: Record<string, string> = {};

          parsed.error.issues.forEach((issue) => {
            const field = issue.path[0];
            if (typeof field === 'string') {
              errors[field] = issue.message;
            }
          });

          return { fields: errors };
        }
        login(parsed.data);
      },
    },
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    form.handleSubmit();
  };

  return (
    <>
      <div className="w-full flex flex-col justify-between items-center gap-10">
        <div className="flex flex-col justify-center items-center gap-1">
          <h1 className="[font-family:var(--font-arvo)]! [font-size:var(--fs-h3)]! md:[font-size:var(--fs-h2)]! xl:[font-size:var(--fs-h1)]! leading-(--lh-h)! font-bold! m-0! text-(--neutral-black-NB1000)!">
            Welcome back
          </h1>
          <p className="[font-family:var(--font-montserrat)]! [font-size:var(--fs-p3)]! leading-(--lh-p)! font-medium! m-0! text-(--neutral-black-NB900)!">
            Please login to your account
          </p>
        </div>
        <form data-test-id="login-from" onSubmit={onSubmit} className="w-full flex flex-1 flex-col">
          <div className="w-full flex flex-col">
            <form.AppField
              name="email"
              children={(field) => (
                <field.Email label="Email" placeholder="Enter your email address" />
              )}
            />

            <form.AppField
              name="password"
              children={(field) => (
                <field.Password label="Password" placeholder="Enter your password" />
              )}
            />
          </div>

          <div className="flex flex-col -mt-2.5 xl:-mt-4.75 gap-10">
            <Link
              to="/forgot-password"
              className="[font-size:var(--fs-b4)] md:[font-size:var(--fs-b3)] bold text-(--primary-O900)! self-end"
            >
              Forgot password?
            </Link>
            <form.AppForm>
              <form.SubmitButton label="Login" />
            </form.AppForm>
          </div>
        </form>
      </div>
      <div className="flex items-center gap-1 self-center">
        <p className="[font-size:var(--fs-l4)] md:[font-size:var(--fs-l3)] text-(--neutral-white-NW600) m-0!">
          Don't have an account?
        </p>
        <Link
          to="/signup"
          className="bold underline! [font-size:var(--fs-l4)] md:[font-size:var(--fs-l3)] text-(--primary-O900)!"
        >
          Create an account
        </Link>
      </div>
    </>
  );
};
